import type { AppNavItem, EntityRestriction } from './appNav';

/**
 * Visibility predicate for top-level nav items, shared by the Sidebar (which
 * filters the rendered links) and useHasNavAccess (the boolean "sidebar is
 * non-empty" form) so the two can never drift apart.
 */

export interface PermissionMap {
  has: (resource: string, action: string) => boolean;
}

export interface UserContext {
  superAdmin: boolean;
  merchant: boolean;
  merchantParent: boolean;
  multiMerchant: boolean;
  vendor: boolean;
}

function matchesRestriction(r: EntityRestriction, ctx: UserContext): boolean {
  switch (r) {
    case 'superAdmin':
      return ctx.superAdmin;
    case 'merchantParent':
      return ctx.merchantParent;
    case 'merchant':
      return ctx.merchant;
    case 'multiMerchant':
      return ctx.multiMerchant;
    case 'vendorOrSuperAdmin':
      // Faithful to the legacy `(superAdmin || vendor)` role gate (e.g. the
      // Shipments nav item) — merchants are fully excluded.
      return ctx.superAdmin || ctx.vendor;
  }
}

export function isNavItemVisible(
  item: AppNavItem,
  permissions: PermissionMap,
  ctx: UserContext,
  hasFlag: (flag: string, defaultValue: boolean) => boolean,
): boolean {
  if (item.requiresFlag && !hasFlag(item.requiresFlag.flag, item.requiresFlag.defaultValue)) {
    return false;
  }
  if (item.requires) {
    // A single requirement, or an array satisfied when the user holds ANY of them.
    const reqs = Array.isArray(item.requires) ? item.requires : [item.requires];
    if (!reqs.some((r) => permissions.has(r.resource, r.action))) {
      return false;
    }
  }
  if (!item.restrictTo?.length) return true;
  return item.restrictTo.every((r) => matchesRestriction(r, ctx));
}
